import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink, Pause, Play, LayoutDashboard, CreditCard, FileText, Globe, Mail, PenSquare, LogIn, Plane, Trash2, Plus, ShieldCheck } from "lucide-react";
import TravelEnquiries from "@/pages/admin/TravelEnquiries";
import Admins from "@/pages/admin/Admins";
import { FormDialog, Toggle } from "@/pages/manage/common";
import { SPORTS } from "@/pages/Signup";
import { copyText } from "@/components/kit";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TopBar } from "@/components/Brand";
import { H1, Overline, Panel, Pill, Stat, PageLoader, Field } from "@/components/kit";
import { api, errMsg, gbp, fmtDay, fmtDate, initials } from "@/lib/api";
import { inputCls } from "@/components/kit";
import Plans from "@/pages/admin/Plans";
import Pages from "@/pages/admin/Pages";
import EmailSettings from "@/pages/admin/EmailSettings";
import { SiteContent, SeoSettings } from "@/pages/admin/SiteContent";
import { useSearchParams } from "react-router-dom";

const PauseDialog = ({ team, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.patch(`/platform/teams/${team.id}/status`, { status: "paused", reason });
      toast.success(`${team.name} paused`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!team} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#111827]">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Pause {team?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Admins and players lose access and the public hub shows as paused until you restore it.</p>
        <Field label="Reason (internal)"><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pause-reason-input" /></Field>
        <Button variant="destructive" onClick={submit} className="rounded-full font-bold" data-testid="confirm-pause-btn">Pause team</Button>
      </DialogContent>
    </Dialog>
  );
};

const NEW_TEAM = { team_name: "", sport: "Football", location: "", admin_name: "", admin_email: "", travel_enabled: false };

const TeamRow = ({ t, plans, onPause, onActivate, onPlan, onDelete, onTravel }) => (
  <tr className="border-b border-white/5 transition-colors hover:bg-white/[0.02]" data-testid={`platform-team-row-${t.slug}`}>
    <td className="py-4 pr-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded font-display font-black text-[#0B0F17]" style={{ background: t.primary_color }}>{initials(t.name)}</span>
        <div><p className="font-semibold text-white">{t.name}</p><p className="text-xs text-slate-500">{t.sport} · since {fmtDay(t.created_at)}</p></div>
      </div>
    </td>
    <td className="py-4 pr-4 text-sm text-slate-300">{t.admins.map((a) => a.email).join(", ")}</td>
    <td className="py-4 pr-4">
      <select className={`${inputCls} h-9 w-40`} value={t.plan_status === "comp" ? t.plan_id : ""} disabled={t.has_subscription} onChange={(e) => onPlan(t, e.target.value || null)} data-testid={`team-plan-select-${t.slug}`}>
        <option value="">{t.has_subscription ? `${t.plan_name} (paying)` : "Default plan"}</option>
        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} (free of charge)</option>)}
      </select>
    </td>
    <td className="py-4 pr-4 font-mono text-sm text-slate-300">{t.member_count} / {t.fan_count}</td>
    <td className="py-4 pr-4">
      <Pill tone={t.status === "active" ? "green" : "red"} data-testid={`team-status-badge-${t.slug}`}>{t.status}</Pill>
      {t.status === "paused" && t.paused_reason && <p className="mt-1 text-xs text-slate-500">{t.paused_reason}</p>}
    </td>
    <td className="py-4 text-right">
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" className={`bg-transparent ${t.travel_enabled ? "border-[#CCFF00]/50 text-[#CCFF00]" : "border-white/15 text-slate-400"}`} onClick={() => onTravel(t)} title="Partners in Travel page" data-testid={`toggle-travel-${t.slug}`}><Plane className="mr-1 h-3.5 w-3.5" />{t.travel_enabled ? "Travel on" : "Travel off"}</Button>
        <Button asChild size="sm" variant="outline" className="border-white/15 bg-transparent" data-testid={`open-team-dashboard-${t.slug}`}><Link to={`/manage/${t.id}`}><LogIn className="mr-1 h-3.5 w-3.5" /> Log in</Link></Button>
        <Button asChild size="sm" variant="ghost" data-testid={`view-hub-${t.slug}`}><Link to={`/t/${t.slug}`} target="_blank"><ExternalLink className="h-4 w-4" /></Link></Button>
        {t.status === "active" ? (
          <Button size="sm" variant="outline" className="border-rose-500/40 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => onPause(t)} data-testid={`pause-team-${t.slug}`}><Pause className="mr-1 h-3.5 w-3.5" /> Pause</Button>
        ) : (
          <Button size="sm" className="font-bold" onClick={() => onActivate(t)} data-testid={`activate-team-${t.slug}`}><Play className="mr-1 h-3.5 w-3.5" /> Restore</Button>
        )}
        <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(t)} title="Delete team" data-testid={`delete-team-${t.slug}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </td>
  </tr>
);

const EmailLog = ({ emails }) => (
  <Panel className="mt-8">
    <p className="font-display text-xl font-bold uppercase text-white">Email log</p>
    <div className="mt-4 space-y-2">
      {emails.map((e) => (
        <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2 text-sm" data-testid="email-log-row">
          <span className="text-slate-300">{e.subject} <span className="text-slate-500">→ {e.to}</span></span>
          <span className="flex items-center gap-2"><Pill tone={e.status === "sent" ? "green" : e.status === "failed" ? "red" : "amber"}>{e.status.replace("_", " ")}</Pill><span className="font-mono text-xs text-slate-500">{fmtDate(e.created_at)}</span></span>
        </div>
      ))}
      {!emails.length && <p className="text-sm text-slate-500">No emails sent yet.</p>}
    </div>
  </Panel>
);

const ADMIN_TABS = [
  { key: "overview", label: "Teams & stats", icon: LayoutDashboard, C: PlatformOverview },
  { key: "plans", label: "Plans", icon: CreditCard, C: Plans },
  { key: "travel", label: "Travel enquiries", icon: Plane, C: TravelEnquiries },
  { key: "content", label: "Site content", icon: PenSquare, C: SiteContent },
  { key: "pages", label: "Pages", icon: FileText, C: Pages },
  { key: "seo", label: "SEO & branding", icon: Globe, C: SeoSettings },
  { key: "email", label: "Email (SMTP)", icon: Mail, C: EmailSettings },
  { key: "admins", label: "Admins", icon: ShieldCheck, C: Admins },
];

export default function SuperAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const Active = (ADMIN_TABS.find((t) => t.key === tab) || ADMIN_TABS[0]).C;
  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <TopBar><Link to="/dashboard" className="hidden text-sm text-slate-400 hover:text-white sm:inline" data-testid="nav-my-teams-link">My teams</Link></TopBar>
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-8 sm:px-8 lg:flex-row lg:py-12">
        <aside className="lg:sticky lg:top-24 lg:h-fit lg:w-56 lg:shrink-0">
          <Overline>Super-admin</Overline>
          <H1 className="mb-6 mt-2 !text-4xl">Platform</H1>
          <nav className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 lg:mx-0 lg:flex-col lg:px-0">
            {ADMIN_TABS.map((t) => (
              <button key={t.key} onClick={() => setParams({ tab: t.key })} data-testid={`admin-tab-${t.key}`}
                className={`flex shrink-0 items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#CCFF00] text-[#0B0F17]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1" data-testid={`admin-panel-${tab}`}><Active /></main>
      </div>
    </div>
  );
}

function PlatformOverview() {
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [emails, setEmails] = useState([]);
  const [q, setQ] = useState("");
  const [pausing, setPausing] = useState(null);
  const [plans, setPlans] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [nt, setNt] = useState(NEW_TEAM);

  const load = async () => {
    const [s, t, e, p] = await Promise.all([api.get("/platform/stats"), api.get("/platform/teams"), api.get("/platform/emails"), api.get("/platform/plans")]);
    setPlans(p.data);
    setStats(s.data);
    setTeams(t.data);
    setEmails(e.data);
  };
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, []);

  const deleteTeam = async (t) => {
    const typed = window.prompt(`This permanently deletes ${t.name} and all its data. Type the team name to confirm:`);
    if (typed !== t.name) return typed !== null && toast.error("Name didn't match — nothing deleted");
    try {
      await api.delete(`/platform/teams/${t.id}`);
      toast.success(`${t.name} deleted`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleTravel = async (t) => {
    await api.patch(`/platform/teams/${t.id}/travel`, { enabled: !t.travel_enabled });
    toast.success(`Travel page ${t.travel_enabled ? "hidden" : "enabled"} for ${t.name}`);
    load();
  };
  const createTeam = async () => {
    try {
      const { data } = await api.post("/platform/teams", nt);
      copyText(data.invite_url, "Team created — admin invite link copied");
      setAddOpen(false);
      setNt(NEW_TEAM);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const activate = async (t) => {
    await api.patch(`/platform/teams/${t.id}/status`, { status: "active" });
    toast.success(`${t.name} restored`);
    load();
  };

  const assignPlan = async (t, planId) => {
    try {
      await api.patch(`/platform/teams/${t.id}/plan`, { plan_id: planId });
      toast.success(planId ? "Plan given free of charge" : "Back on the default plan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!stats) return <PageLoader />;
  const shown = teams.filter((t) => `${t.name} ${t.sport} ${t.admins.map((a) => a.email).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Stat label="Teams" value={stats.teams} testid="stat-teams" hint={`${stats.paused_teams} paused`} />
          <Stat label="Active" value={stats.active_teams} testid="stat-active-teams" />
          <Stat label="Members" value={stats.members} testid="stat-members" hint={`${stats.pending_invites} invites pending`} />
          <Stat label="Fans" value={stats.fans} testid="stat-fans" />
          <Stat label="Card revenue" value={gbp(stats.stripe_revenue)} testid="stat-revenue" />
          <Stat label="Emails" value={stats.emails_sent} testid="stat-emails" hint={`${stats.emails_failed} not delivered`} />
        </div>
        <Panel className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-display text-xl font-bold uppercase text-white">Team directory</p>
            <Button size="sm" className="rounded-full font-bold" onClick={() => setAddOpen(true)} data-testid="add-team-btn"><Plus className="mr-1 h-4 w-4" /> Add team</Button>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <Input className="pl-9" placeholder="Search teams or admins" value={q} onChange={(e) => setQ(e.target.value)} data-testid="platform-team-search" />
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left">
              <thead><tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-slate-500"><th className="py-3">Team</th><th>Admins</th><th>Plan</th><th>Members / Fans</th><th>Status</th><th /></tr></thead>
              <tbody>{shown.map((t) => <TeamRow key={t.id} t={t} plans={plans} onPause={setPausing} onActivate={activate} onPlan={assignPlan} onDelete={deleteTeam} onTravel={toggleTravel} />)}</tbody>
            </table>
          </div>
        </Panel>
        <EmailLog emails={emails} />
      <PauseDialog team={pausing} onClose={() => setPausing(null)} onDone={() => { setPausing(null); load(); }} />
      <FormDialog open={addOpen} onOpenChange={setAddOpen} title="Add team" onSubmit={createTeam} submitLabel="Create team & invite admin" testid="add-team-form">
        <Field label="Team name"><Input required value={nt.team_name} onChange={(e) => setNt({ ...nt, team_name: e.target.value })} data-testid="add-team-name-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sport"><select className={inputCls} value={nt.sport} onChange={(e) => setNt({ ...nt, sport: e.target.value })} data-testid="add-team-sport-select">{SPORTS.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Location"><Input value={nt.location} onChange={(e) => setNt({ ...nt, location: e.target.value })} data-testid="add-team-location-input" /></Field>
        </div>
        <Field label="Team admin name"><Input required value={nt.admin_name} onChange={(e) => setNt({ ...nt, admin_name: e.target.value })} data-testid="add-team-admin-name-input" /></Field>
        <Field label="Team admin email" hint="They get an email link to set their password"><Input type="email" required value={nt.admin_email} onChange={(e) => setNt({ ...nt, admin_email: e.target.value })} data-testid="add-team-admin-email-input" /></Field>
        <Toggle checked={nt.travel_enabled} onChange={(v) => setNt({ ...nt, travel_enabled: v })} label="Enable Partners in Travel page" testid="add-team-travel-toggle" />
      </FormDialog>
    </>
  );
}
